import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../../components/Card.jsx';
import Button from '../../components/Button.jsx';
import Input from '../../components/Input.jsx';
import Modal from '../../components/Modal.jsx';
import { ArrowLeft, Upload, X, BookOpen, Plus, CheckCircle } from 'lucide-react';
import { apiService } from '../../context/apiService.js';
import { toast } from 'react-toastify';

const CreateCourse = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    category: '',
    level: 'beginner',
    language: 'english',
    thumbnail: null,
    thumbnailPreview: null,
    published: false
  });
  const [originalThumbnail, setOriginalThumbnail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCourse, setLoadingCourse] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdCourseId, setCreatedCourseId] = useState(null);

  // Fetch course data when in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      fetchCourseData();
    }
  }, [isEditMode, id]);

  const fetchCourseData = async () => {
    try {
      setLoadingCourse(true);
      const response = await apiService.courses.getById(id);
      
      if (response.data.success) {
        const course = response.data.course;
        setOriginalThumbnail(course.thumbnail || null);
        setFormData({
          title: course.title || '',
          description: course.description || '',
          price: course.price?.toString() || '',
          category: course.category || '',
          level: course.level || 'beginner',
          language: course.language || 'english',
          thumbnail: null, // Don't pre-populate file input
          thumbnailPreview: course.thumbnail || null, // Show existing thumbnail
          published: course.published || false // Add published status
        });
      } else {
        toast.error(response.data.message || 'Failed to load course data');
        navigate('/teacher/courses');
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course data');
      navigate('/teacher/courses');
    } finally {
      setLoadingCourse(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        thumbnail: file,
        thumbnailPreview: URL.createObjectURL(file)
      }));
      
      // Clear thumbnail error when file is selected
      if (errors.thumbnail) {
        setErrors(prev => ({
          ...prev,
          thumbnail: ''
        }));
      }
    }
  };

  const removeThumbnail = () => {
    if (isEditMode) {
      // In edit mode:
      // - If a new thumbnail is selected, revert to the original
      // - If no new thumbnail is selected, clear the preview to allow choosing a new one
      setFormData(prev => ({
        ...prev,
        thumbnail: null,
        thumbnailPreview: prev.thumbnail ? originalThumbnail : null
      }));
    } else {
      // In create mode, remove everything
      setFormData(prev => ({
        ...prev,
        thumbnail: null,
        thumbnailPreview: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Course title is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Course description is required';
    }
    
    if (!formData.price.trim()) {
      newErrors.price = 'Course price is required';
    } else if (isNaN(parseFloat(formData.price)) || parseFloat(formData.price) < 0) {
      newErrors.price = 'Please enter a valid price';
    }
    
    if (!formData.category.trim()) {
      newErrors.category = 'Course category is required';
    }

    if (!isEditMode && !formData.thumbnail) {
      newErrors.thumbnail = 'Course thumbnail is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('price', parseFloat(formData.price));
      formDataToSend.append('category', formData.category);
      
      // Only append thumbnail if a new one is selected
      if (formData.thumbnail) {
        formDataToSend.append('thumbnail', formData.thumbnail);
      }
      
      console.log(`${isEditMode ? 'Updating' : 'Creating'} course with data:`, {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price),
        category: formData.category,
        thumbnail: formData.thumbnail ? formData.thumbnail.name : 'No new thumbnail'
      });
      
      let response;
      if (isEditMode) {
        response = await apiService.courses.update(id, formDataToSend);
      } else {
        response = await apiService.courses.create(formDataToSend);
      }
      
      if (response.data.success) {
        toast.success(`Course ${isEditMode ? 'updated' : 'created'} successfully!`);
        
        if (isEditMode) {
          // For edit mode, just navigate back to courses
          navigate('/teacher/courses');
        } else {
          // For create mode, show success modal with options
          setCreatedCourseId(response.data.course._id);
          setShowSuccessModal(true);
        }
      } else {
        toast.error(response.data.message || `Failed to ${isEditMode ? 'update' : 'create'} course`);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} course:`, error);
      const errorMessage = error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} course. Please try again.`;
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while fetching course data in edit mode
  if (loadingCourse) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-secondary-600">Loading course data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/teacher/courses')}
          className="p-2 text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">
            {isEditMode ? 'Edit Course' : 'Create New Course'}
          </h1>
          <p className="text-secondary-600 mt-1">
            {isEditMode ? 'Update your course details and content' : 'Set up your course details and start building content'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card>
              <Card.Header>
                <Card.Title>Basic Information</Card.Title>
                <Card.Description>
                  Provide essential details about your course
                </Card.Description>
              </Card.Header>
              <Card.Content className="space-y-4">
                <Input
                  label="Course Title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter an engaging course title"
                  error={errors.title}
                  required
                />

                <div>
                  <label className="label text-secondary-700 mb-2 block">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Describe what students will learn in this course"
                    rows={4}
                    className={`input ${errors.description ? 'border-red-500' : ''}`}
                    required
                  />
                  {errors.description && (
                    <p className="text-sm text-red-600 mt-1">{errors.description}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Price (₹)"
                    name="price"
                    type="number"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="0.00"
                    error={errors.price}
                    required
                  />

                  <div>
                    <label className="label text-secondary-700 mb-2 block">Category</label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className={`input ${errors.category ? 'border-red-500' : ''}`}
                      required
                    >
                      <option value="">Select a category</option>
                      <option value="web-development">Web Development</option>
                      <option value="mobile-development">Mobile Development</option>
                      <option value="data-science">Data Science</option>
                      <option value="design">Design</option>
                      <option value="business">Business</option>
                      <option value="marketing">Marketing</option>
                      <option value="other">Other</option>
                    </select>
                    {errors.category && (
                      <p className="text-sm text-red-600 mt-1">{errors.category}</p>
                    )}
                  </div>
                </div>
              </Card.Content>
            </Card>

            {/* Course Settings */}
            <Card>
              <Card.Header>
                <Card.Title>Course Settings</Card.Title>
                <Card.Description>
                  Configure additional course options
                </Card.Description>
              </Card.Header>
              <Card.Content className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label text-secondary-700 mb-2 block">Difficulty Level</label>
                    <select
                      name="level"
                      value={formData.level}
                      onChange={handleChange}
                      className="input"
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </div>

                  <div>
                    <label className="label text-secondary-700 mb-2 block">Language</label>
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleChange}
                      className="input"
                    >
                      <option value="english">English</option>
                      <option value="spanish">Spanish</option>
                      <option value="french">French</option>
                      <option value="german">German</option>
                    </select>
                  </div>
                </div>
              </Card.Content>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Course Thumbnail */}
            <Card>
              <Card.Header>
                <Card.Title>Course Thumbnail</Card.Title>
                <Card.Description>
                  {isEditMode ? 'Update thumbnail image (optional)' : 'Upload an engaging thumbnail image'}
                </Card.Description>
              </Card.Header>
              <Card.Content>
                {formData.thumbnailPreview ? (
                  <div className="relative">
                    <img
                      src={formData.thumbnailPreview}
                      alt="Course thumbnail"
                      className="w-full aspect-video object-cover object-center rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={removeThumbnail}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      title={isEditMode ? (formData.thumbnail ? "Remove new thumbnail (revert to current)" : "Remove current thumbnail") : "Remove thumbnail"}
                    >
                      <X size={16} />
                    </button>
                    {isEditMode && (
                      <div className="absolute bottom-2 left-2 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-xs">
                        {formData.thumbnail ? 'New thumbnail' : 'Current thumbnail'}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center ${
                    errors.thumbnail ? 'border-red-500' : 'border-secondary-300'
                  }`}>
                    <Upload className="w-8 h-8 text-secondary-400 mx-auto mb-2" />
                    <p className="text-sm text-secondary-600 mb-2">Upload thumbnail</p>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailChange}
                      className="hidden"
                      id="thumbnail-upload"
                    />
                    <label
                      htmlFor="thumbnail-upload"
                      className="btn btn-outline btn-sm cursor-pointer"
                    >
                      Choose File
                    </label>
                  </div>
                )}
                {errors.thumbnail && (
                  <p className="text-sm text-red-600 mt-2">{errors.thumbnail}</p>
                )}
              </Card.Content>
            </Card>

            {/* Course Preview */}
            <Card>
              <Card.Header>
                <Card.Title>Course Preview</Card.Title>
              </Card.Header>
              <Card.Content>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-secondary-700">Status</p>
                    <p className="text-sm text-secondary-600">
                      {isEditMode ? (formData.published ? 'Published' : 'Draft') : 'Draft'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-secondary-700">
                      {isEditMode ? 'Last Updated' : 'Created'}
                    </p>
                    <p className="text-sm text-secondary-600">
                      {isEditMode ? 'Just now' : 'Just now'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-secondary-700">Lessons</p>
                    <p className="text-sm text-secondary-600">0 lessons</p>
                  </div>
                </div>
              </Card.Content>
            </Card>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/teacher/courses')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={loading}
            disabled={loading || loadingCourse}
          >
            {loading ? (isEditMode ? 'Updating Course...' : 'Creating Course...') : (isEditMode ? 'Update Course' : 'Create Course')}
          </Button>
        </div>
      </form>

      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Course Created Successfully!"
      >
        <div className="text-center py-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          
          <h3 className="text-xl font-semibold text-secondary-900 mb-2">
            Your course has been created!
          </h3>
          
          <p className="text-secondary-600 mb-6">
            Great! Your course is now ready. You can start adding lessons to make it complete.
          </p>

          <div className="space-y-3">
            <Button
              onClick={() => navigate(`/teacher/courses/${createdCourseId}/lessons`)}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Lessons to Course
            </Button>
            
            <Button
              variant="outline"
              onClick={() => navigate('/teacher/courses')}
              className="w-full"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              View All Courses
            </Button>
            
            <Button
              variant="ghost"
              onClick={() => setShowSuccessModal(false)}
              className="w-full"
            >
              Continue Editing Course
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CreateCourse;
